import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import Pagination from 'components/Pagination/Pagination';
import { useState } from 'react';
import useSWR from 'swr';
import { formatDate } from 'utils/date';
import { adminLogsService } from 'views/services/adminLogsService';
import { IAdminLog } from 'views/types/adminLogs';
import { formatDetails } from './formatters';
import clsx from 'clsx';
import { Button } from 'components/button';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';

const columnHelper = createColumnHelper<IAdminLog>();
const columns = [
  columnHelper.accessor('action_type', {
    header: 'Action Type',
    cell: info => {
      const actionType = info.getValue();

      const getActionStyle = (type: string) => {
        if (type.includes('CREATE')) {
          return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
        }
        if (type.includes('UPDATE')) {
          return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        }
        if (type.includes('DELETE')) {
          return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
        }
        if (type.includes('BLOCK') || type.includes('UNBLOCK')) {
          return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
        }
        if (type.includes('APPROVE') || type.includes('REJECT')) {
          return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
        }
        if (type.includes('VIEW') || type.includes('KYC_VIEW')) {
          return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200';
        }
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      };

      return (
        <span
          className={clsx(
            'px-2 py-1 rounded-full text-xs font-medium',
            getActionStyle(actionType)
          )}
        >
          {actionType.replace(/_/g, ' ')}
        </span>
      );
    },
  }),
  columnHelper.accessor('target_entity_type', {
    header: 'Target Entity',
    cell: info => {
      const entityType = info.getValue();
      if (!entityType) return <span className="text-gray-500">--</span>;

      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
          {entityType.replace(/_/g, ' ')}
        </span>
      );
    },
  }),
  columnHelper.accessor('description', {
    header: 'Description',
    cell: info => {
      const description = info.getValue();
      return (
        <span className="text-gray-700 dark:text-gray-300">
          {description || '--'}
        </span>
      );
    },
  }),
  columnHelper.accessor('target_entity_id', {
    header: 'Details',
    cell: info => {
      const row = info.row.original;
      const details = formatDetails(row);

      const renderDetails = (text: string) => {
        const lines = text.split('\n');
        return (
          <>
            {lines.map((line, idx) => {
              const parts = line.split(', ').filter(Boolean);
              const loginIndex = parts.findIndex(p =>
                p.toLowerCase().startsWith('login:')
              );
              const loginPart =
                loginIndex >= 0 ? parts.splice(loginIndex, 1)[0] : undefined;
              const loginValue = loginPart
                ? loginPart.split(':').slice(1).join(':').trim()
                : undefined;
              return (
                <div key={idx} className="mb-0.5">
                  {loginPart && (
                    <>
                      <span>Login: </span>
                      <strong className="font-semibold">{loginValue}</strong>
                      {parts.length > 0 ? (
                        <span>{`, ${parts.join(', ')}`}</span>
                      ) : null}
                    </>
                  )}
                  {!loginPart && <span>{parts.join(', ')}</span>}
                </div>
              );
            })}
          </>
        );
      };

      return (
        <div className="max-w-md">
          <div className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
            {renderDetails(details)}
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Date + time',
    cell: info => (
      <span className="font-medium text-gray-700 dark:text-gray-300">
        {formatDate(info.getValue(), 'DD.MM.YYYY HH:mm')}
      </span>
    ),
  }),
];

interface AdminLogsProps {
  adminId: string;
}

export const AdminLogs = ({ adminId }: AdminLogsProps) => {
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const { data: logsData, isLoading } = useSWR(
    `/admin-logs?page=${page}&limit=${itemsPerPage}&adminId=${adminId}`,
    () =>
      adminLogsService.getPaginatedAdminLogsList({
        page,
        pageSize: itemsPerPage,
        filter: {
          admin_id: `$eq:${adminId}`,
        },
      }),
    {
      refreshInterval: 10000,
    }
  );

  const { canUpdate } = usePermissions();

  const table = useReactTable({
    data: logsData?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  const totalPages = Math.ceil((logsData?.meta.totalItems ?? 0) / itemsPerPage);

  const handleExport = () => {
    adminLogsService.exportAdminLogsToCsv({
      admin_id: adminId,
    });
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            Admin Activity Logs
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Track all administrative actions and changes
          </p>
        </div>
        <ReadPermissionGate resource={PERMISSION_RESOURCES.ADMIN_LOGS}>
          <Button
            disabled={
              !canUpdate(PERMISSION_RESOURCES.ADMIN_LOGS) ||
              !canUpdate(PERMISSION_RESOURCES.ADMINS)
            }
            onClick={handleExport}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Export CSV
          </Button>
        </ReadPermissionGate>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-600 dark:text-gray-400">
              Loading logs...
            </span>
          </div>
        </div>
      ) : logsData?.data?.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No logs found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            This admin hasn't performed any actions yet
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <table className="min-h-12 min-w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="pb-4 px-6 pt-4 text-start font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white dark:bg-gray-900">
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                    index % 2 === 0
                      ? 'bg-white dark:bg-gray-900'
                      : 'bg-gray-25 dark:bg-gray-850'
                  }`}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="p-4 align-top">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={logsData?.meta.totalItems}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalName="Total Admin Logs"
            isBottomIndicators
          />
        </div>
      )}
    </>
  );
};
